import { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import type { NoteAudience } from "@shyne/shared/api";
import NoteAudiencePicker from "@/components/shared/NoteAudiencePicker";
import { BaseAppointmentAction, AppointmentActionContext, UserRole } from "./actions";
import { isReschedulableDay } from "@/lib/appointment-reschedule";
import { firstGoBackDay, isGoBackDay } from "@/lib/appointment-go-back";

interface ActionDialogProps {
  action: BaseAppointmentAction;
  context: AppointmentActionContext;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  hookState: {
    newDate?: Date;
    setNewDate: (date: Date | undefined) => void;
    comment: string;
    setComment: (comment: string) => void;
    reason: string;
    setReason: (reason: string) => void;
    selectedStatus: string;
    setSelectedStatus: (status: string) => void;
    commentAudience: NoteAudience[];
    setCommentAudience: (audience: NoteAudience[]) => void;
    parentCategories: string[];
    selectedParent: string;
    setSelectedParent: (parent: string) => void;
    availableServices: { id: string; name: string }[];
    selectedService: string;
    setSelectedService: (service: string) => void;
    selectedServiceName: string;
    setSelectedServiceName: (name: string) => void;
    loadingServices: boolean;
    isConfirmDisabled: () => boolean;
    getConfirmButtonText: () => string;
    getAvailableStatuses: () => string[];
  };
}

export default function ActionDialog({ action, context, open, onConfirm, onCancel, hookState }: ActionDialogProps) {
  const {
    newDate,
    setNewDate,
    comment,
    setComment,
    reason,
    setReason,
    selectedStatus,
    setSelectedStatus,
    commentAudience,
    setCommentAudience,
    parentCategories,
    selectedParent,
    setSelectedParent,
    availableServices,
    selectedService,
    setSelectedService,
    selectedServiceName,
    setSelectedServiceName,
    loadingServices,
    isConfirmDisabled,
    getConfirmButtonText,
    getAvailableStatuses,
  } = hookState;
  useEffect(() => {
    if (!open) {
      document.body.style.pointerEvents = "";
    }
  }, [open]);

  const currentDate = context.currentDate ?? "";
  const currentMonth = context.currentDate ? new Date(`${context.currentDate}T00:00:00`) : undefined;
  const isClosedDay = (day: Date) => !isReschedulableDay(day, currentDate);

  const chooseStatus = (status: string) => {
    setSelectedStatus(status);
    if (status === "goback" && !newDate) setNewDate(firstGoBackDay());
  };

  const renderDialogContent = () => {
    switch (action.config.id) {
      case "reschedule":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a new date for this appointment. It will return to pending and assignment will restart.
            </p>
            <Calendar
              mode="single"
              selected={newDate}
              defaultMonth={newDate ?? currentMonth}
              onSelect={setNewDate}
              disabled={isClosedDay}
              className="rounded-md border"
            />
          </div>
        );

      case "reschedule_request":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Request a reschedule from the PMC. Select your preferred new date.
            </p>
            <Calendar
              mode="single"
              selected={newDate}
              defaultMonth={newDate ?? currentMonth}
              onSelect={setNewDate}
              disabled={isClosedDay}
              className="rounded-md border"
            />
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                This will send a request to the PMC for approval. They can approve or reject your request.
              </p>
            </div>
          </div>
        );

      case "add_comment":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Add a communication note to this appointment.
            </p>
            <Textarea
              placeholder="Enter your note..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
            />
            {context.userRole === UserRole.VENDOR && (
              <NoteAudiencePicker audience={commentAudience} onChange={setCommentAudience} idPrefix="dialog-audience" />
            )}
          </div>
        );

      case "cancel":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to cancel this appointment? This action cannot be undone.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> Cancelling this appointment will notify all participants and update the status
                to "cancelled".
              </p>
            </div>
          </div>
        );

      case "cancel_request":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Request cancellation of this appointment from the PMC. Please provide a reason for the request.
            </p>
            <Textarea
              placeholder="Reason for cancellation (optional)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
            <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
              <p className="text-sm text-orange-800">
                This will send a cancellation request to the PMC for approval. They can approve or reject your request.
              </p>
            </div>
          </div>
        );

      case "status_change":
        const availableStatuses = getAvailableStatuses();
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Change the appointment status. Current status: <Badge variant="outline">{context.currentStatus}</Badge>
            </p>
            <Select value={selectedStatus} onValueChange={chooseStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {availableStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    <div className="flex items-center gap-2">
                      {status}
                      {status !== context.currentStatus && (
                        <span className="text-xs text-muted-foreground">(from {context.currentStatus})</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedStatus === "goback" && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Pick the date to go back.</p>
                <Calendar
                  mode="single"
                  selected={newDate}
                  defaultMonth={newDate}
                  onSelect={setNewDate}
                  disabled={(day) => !isGoBackDay(day)}
                  className="rounded-md border"
                />
              </div>
            )}
          </div>
        );

      case "change_service":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Request a service change from the current vendor. This requires approval from the{" "}
              {context.userRole === "pmc" ? "vendor" : "PMC"}.
            </p>

            {loadingServices && parentCategories.length === 0 ? (
              <div className="text-sm text-muted-foreground">Loading service categories...</div>
            ) : (
              <div className="space-y-3">
                <Select value={selectedParent} onValueChange={setSelectedParent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select service category" />
                  </SelectTrigger>
                  <SelectContent>
                    {parentCategories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedService}
                  onValueChange={(value) => {
                    setSelectedService(value);
                    const service = availableServices.find((s) => s.id === value);
                    setSelectedServiceName(service?.name || "");
                  }}
                  disabled={!selectedParent}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specific service" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingServices && selectedParent ? (
                      <SelectItem value="loading" disabled>
                        Loading services...
                      </SelectItem>
                    ) : (
                      availableServices.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                Service changes require approval. The {context.userRole === "pmc" ? "vendor" : "PMC"} will be notified
                of your request.
              </p>
            </div>
          </div>
        );

      case "request_reassignment":
        return (
          <div className="space-y-4">
            <Textarea
              placeholder="Reason for reassignment request..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
            <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
              <p className="text-sm text-orange-800">
                You come off this appointment as soon as you send. The vendor sees your reason in this chat and assigns
                someone new.
              </p>
            </div>
          </div>
        );

      case "request_service_change":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Request a service change from the PMC. This requires approval from the property management company.
            </p>

            {loadingServices && parentCategories.length === 0 ? (
              <div className="text-sm text-muted-foreground">Loading service categories...</div>
            ) : (
              <div className="space-y-3">
                <Select value={selectedParent} onValueChange={setSelectedParent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select service category" />
                  </SelectTrigger>
                  <SelectContent>
                    {parentCategories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedService}
                  onValueChange={(value) => {
                    setSelectedService(value);
                    const service = availableServices.find((s) => s.id === value);
                    setSelectedServiceName(service?.name || "");
                  }}
                  disabled={!selectedParent}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specific service" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingServices && selectedParent ? (
                      <SelectItem value="loading" disabled>
                        Loading services...
                      </SelectItem>
                    ) : (
                      availableServices.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                Service change requests require approval from the PMC. They will be notified of your request.
              </p>
            </div>
          </div>
        );

      default:
        return (
          <p className="text-sm text-muted-foreground">
            {action.config.description || `Confirm this action: ${action.config.label}`}
          </p>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          document.body.style.pointerEvents = "";
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <action.config.icon className="w-5 h-5" />
            {action.config.label}
          </DialogTitle>
          {action.config.description && <DialogDescription>{action.config.description}</DialogDescription>}
        </DialogHeader>

        {renderDialogContent()}

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isConfirmDisabled()}
            variant={action.config.variant === "destructive" ? "destructive" : "default"}
          >
            {getConfirmButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
